import React, { Children } from 'react';
import { motion } from 'framer-motion';
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
};
const cardVariants = {
  hidden: {
    opacity: 0,
    y: 24
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.7,
      ease: 'easeOut'
    }
  }
};
export function PainPointsSection() {
  const cards = [{
    num: '01',
    title: 'The strategy is clear at the top',
    subtitle: 'VISION · DECISIONS · DIRECTION',
    text: 'You have done the strategic thinking. The vision is there. The choices have been made. But somewhere between your leadership table and the people doing the work, the strategy starts falling apart. The results coming back are not what you expected. Not because the strategy was wrong, but because something breaks down on the way to execution.'
  }, {
    num: '02',
    title: 'The people in the middle are not aligned',
    subtitle: 'COMMUNICATION · ENGAGEMENT · OWNERSHIP',
    text: 'This is where it breaks. Leaders communicate the strategy and assume alignment happened. But people are busy moving in different directions, not hitting the right targets, not taking ownership. The conversations are not clear enough, not frequent enough, and not honest enough. People nod, but they are not aligned. They are directed, not engaged. And it keeps happening over and over.'
  }, {
    num: '03',
    title: 'The results at the bottom do not match the plan',
    subtitle: 'EXECUTION · ACCOUNTABILITY · FEEDBACK',
    text: 'The gap between strategy and results is not a strategy problem. It is a people problem. Without real alignment, honest feedback, and consistent accountability, execution stays messy. Leaders keep repairing strategy when what actually needs repair is how they communicate, engage, and develop the people responsible for carrying it out.'
  }];
  return <section className="py-16 md:py-24 bg-white" data-id="element-236">
      <div className="max-w-[1200px] mx-auto px-6 md:px-12" data-id="element-237">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.6
      }} className="text-center mb-16 md:mb-24" data-id="element-238">
          <div className="flex items-center justify-center gap-4 mb-6" data-id="element-239">
            <div className="w-12 h-[1.5px] bg-gold" data-id="element-240" />
            <span className="font-heading font-bold text-[11px] text-gold uppercase tracking-[0.1em]" data-id="element-241">
              The Challenge
            </span>
            <div className="w-12 h-[1.5px] bg-gold" data-id="element-242" />
          </div>
          <h2 className="text-[28px] md:text-[36px] tracking-[-0.02em]" data-id="element-243">
            Where leadership results break down
          </h2>
        </motion.div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12 md:mb-16" data-id="element-244">
          {cards.map((card, index) => <motion.div key={index} variants={cardVariants} className="relative bg-white border-[0.5px] border-warm-border border-t-2 border-t-gold rounded-xl p-8 lg:p-10 flex flex-col h-full group" data-id="element-245">
              {/* Large faded number */}
              <span className="absolute top-6 right-8 font-heading font-bold text-[72px] leading-none text-gold/[0.07] select-none" data-id="element-246">
                {card.num}
              </span>

              <span className="font-heading font-bold text-[17px] text-gold uppercase tracking-[0.1em] mb-5 block relative z-10" data-id="element-247">
                {card.subtitle}
              </span>
              <h3 className="text-[18px] md:text-[20px] mb-6 leading-snug relative z-10" data-id="element-248">
                {card.title}
              </h3>
              <p className="text-charcoal text-[15px] md:text-[16px] leading-[1.75] flex-grow relative z-10" data-id="element-249">
                {card.text}
              </p>
            </motion.div>)}
        </motion.div>

        <motion.div initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8,
        delay: 0.4
      }} className="max-w-[680px] mx-auto text-center bg-warm-gray/60 rounded-xl py-8 px-8 md:px-12 border-[0.5px] border-warm-border" data-id="element-250">
          <div className="w-10 h-[2px] bg-gold mx-auto mb-5" data-id="element-251" />
          <p className="font-subtitle italic text-[17px] md:text-[20px] text-navy leading-relaxed" data-id="element-252">
            The missing link between strategy and execution is not a better
            plan. It is leadership capacity: the ability to align people,
            develop others, and turn conversations into real action. That is
            what coaching repairs.
          </p>
        </motion.div>
      </div>
    </section>;
}